import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import PageHeading from '../../components/PageHeading';
import {
  ALL_ACCOUNTS, cleanAccountName, getMyAccountBalances, getMyPerformance, getMyPositions,
} from '../../components/dashboard/myData/myDataApi';
import { usd, pct, tone, shortDate, niceDate, DOWN, BrokerMark } from '../../components/dashboard/myData/myWidgetUi';
import { ProUpsell } from '../personal/PersonalCommon';
import {
  positionSize, concentration, equityRisk, stressLoss, holdingsBySymbol, planStop, rebalance,
} from './portfolioRiskMath';
import '../personal/personal.css';
import './portfolioRisk.css';

/*
 * Portfolio risk tools: for stock, ETF, options-account and crypto holdings in
 * the user's connected brokerage accounts. (The prop-firm tools, Blowout Risk,
 * % Rule Risk and Hedging Risk, are for futures evaluation accounts.)
 */

export const PORTFOLIO_RISK_TOOLS = {
  'position-sizer': {
    title: 'Position Sizer',
    description: 'How many shares to buy so a stop-out costs only what you choose to risk.',
  },
  'concentration-risk': {
    title: 'Concentration Risk',
    description: 'How much of your money rides on a few holdings, and what a market drop would cost.',
  },
  'drawdown-risk': {
    title: 'Drawdown & VaR',
    description: 'How far your accounts fall from their highs, how much they swing, and a typical bad day.',
  },
  'stop-planner': {
    title: 'Stop Planner',
    description: 'Plan a stop for each holding and see what you’d lose, or lock in, if they’re hit.',
  },
  'rebalance': {
    title: 'Rebalancing',
    description: 'Set target weights and get the buys and sells that bring your holdings back in line.',
  },
};

/* Plans are a convenience kept in this browser only. */
function useStoredState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initial;
    } catch (_err) {
      return initial;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_err) { /* private mode */ }
  }, [key, value]);
  return [value, setValue];
}

const axisTick = { fontSize: 11, fill: 'var(--myr-axis, #94a3b8)' };
const tipStyle = { background: 'rgba(2,6,23,0.94)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 9, fontSize: 12 };
const num = (v) => (v === '' || v == null ? '' : Number(v));

/** Load once with loading / error / Pro states. */
function useLoad(fn, deps, enabled = true) {
  const [state, setState] = useState({ loading: enabled, error: '', requiresPro: false, data: null });
  useEffect(() => {
    if (!enabled) { setState({ loading: false, error: '', requiresPro: false, data: null }); return undefined; }
    let live = true;
    setState((s) => ({ ...s, loading: true, error: '' }));
    fn()
      .then((data) => { if (live) setState({ loading: false, error: '', requiresPro: false, data }); })
      .catch((err) => { if (live) setState({ loading: false, error: err?.message || 'Couldn’t load your accounts.', requiresPro: Boolean(err?.requiresPro), data: null }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);
  return state;
}

function Kpi({ label, value, sub, cls = '' }) {
  return (
    <div>
      <span>{label}</span>
      <b className={cls}>{value}{sub ? <em> {sub}</em> : null}</b>
    </div>
  );
}

function Status({ state, onNavigate, empty }) {
  if (state.loading) return <div className="myr-state"><span>Loading your accounts…</span></div>;
  if (state.error) return <div className="myr-state"><strong>Couldn’t load your accounts</strong><span>{state.error}</span></div>;
  if (empty) {
    return (
      <div className="myr-state">
        <strong>No connected accounts yet</strong>
        <span>Connect a brokerage (Schwab, Robinhood, Webull, Fidelity, Coinbase and more) to use this with your real holdings.</span>
        {onNavigate ? <button type="button" className="mys-btn is-primary" onClick={() => onNavigate('home')}>Connect an account</button> : null}
      </div>
    );
  }
  return null;
}

/* Position sizer ----------------------------------------------------------- */

function PositionSizer({ isPro }) {
  const balances = useLoad(() => getMyAccountBalances('1M'), [], isPro);
  const positions = useLoad(() => getMyPositions(ALL_ACCOUNTS), [], isPro);
  const accounts = useMemo(() => (balances.data || []).filter((a) => a.equity > 0), [balances.data]);
  const total = accounts.reduce((s, a) => s + a.equity, 0);
  const [source, setSource] = useState('');
  const [custom, setCustom] = useState('25000');
  const [riskPct, setRiskPct] = useState(1);
  const [symbol, setSymbol] = useState('');
  const [entry, setEntry] = useState('');
  const [stop, setStop] = useState('');
  const [target, setTarget] = useState('');
  const [maxPos, setMaxPos] = useState(25);

  const pick = source || (accounts.length ? 'all' : 'custom');
  const accountValue = pick === 'custom' ? Number(custom) || 0
    : pick === 'all' ? total
      : accounts.find((a) => a.id === pick)?.equity || 0;
  const r = positionSize({ accountValue, riskPct: Number(riskPct), entry: num(entry), stop: num(stop), target: num(target), maxPositionPct: Number(maxPos) });
  const held = useMemo(() => {
    const sym = symbol.trim().toUpperCase();
    if (!sym || !positions.data) return null;
    const v = positions.data.positions.filter((p) => String(p.symbol).toUpperCase() === sym)
      .reduce((s, p) => s + (Number(p.marketValue) || 0), 0);
    return v ? v : null;
  }, [symbol, positions.data]);

  return (
    <div className="prk-grid">
      <section className="myr-card">
        <div className="myr-card-head"><h3>Your trade</h3></div>
        <div className="prk-form">
          <label className="prk-wide">Account size
            <select value={pick} onChange={(e) => setSource(e.target.value)}>
              {accounts.length ? <option value="all">All connected accounts · {usd(total)}</option> : null}
              {accounts.map((a) => <option key={a.id} value={a.id}>{cleanAccountName(a.name)} · {usd(a.equity)}</option>)}
              <option value="custom">Type an amount</option>
            </select>
          </label>
          {pick === 'custom' ? <label className="prk-wide">Amount <input type="number" min="0" value={custom} onChange={(e) => setCustom(e.target.value)} /></label> : null}
          <label className="prk-wide">Risk per trade (% of account)
            <div className="prk-risk-row">
              <div className="mydw-pills">
                {[0.25, 0.5, 1, 2].map((v) => <button key={v} type="button" className={Number(riskPct) === v ? 'active' : ''} onClick={() => setRiskPct(v)}>{v}%</button>)}
              </div>
              <input type="number" min="0" step="0.05" value={riskPct} onChange={(e) => setRiskPct(e.target.value)} aria-label="Risk percent" />
            </div>
          </label>
          <label>Symbol (optional) <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="AAPL" /></label>
          <label>Entry price <input type="number" min="0" step="0.01" value={entry} onChange={(e) => setEntry(e.target.value)} /></label>
          <label>Stop price <input type="number" min="0" step="0.01" value={stop} onChange={(e) => setStop(e.target.value)} /></label>
          <label>Target (optional) <input type="number" min="0" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
          <label>Max position (% of account) <input type="number" min="0" max="100" value={maxPos} onChange={(e) => setMaxPos(e.target.value)} /></label>
        </div>
        {!isPro ? <p className="mys-muted">With Pro, your connected accounts’ values fill in here automatically.</p> : null}
      </section>
      <section className="myr-card">
        <div className="myr-card-head"><h3>Size it</h3></div>
        {!r ? (
          <p className="mys-muted">Enter an account size, entry and stop to see how many shares to buy.</p>
        ) : (
          <>
            <div className="prk-answer">
              <span>{r.side === 'long' ? 'Buy' : 'Sell short'}</span>
              <b>{r.wholeShares.toLocaleString()} {r.wholeShares === 1 ? 'share' : 'shares'}</b>
              {r.wholeShares < 1 ? <small>{r.shares.toFixed(4)} if your broker allows fractional shares or coins</small> : null}
            </div>
            <div className="myr-kpis">
              <Kpi label="Lose if stopped" value={usd(r.actualRisk, { cents: true })} sub={pct(r.actualRisk / accountValue, { sign: false })} cls="is-down" />
              <Kpi label="Position value" value={usd(r.positionValue)} sub={`${pct(r.positionPct, { sign: false })} of account`} />
              <Kpi label="Stop distance" value={usd(r.perShare, { cents: true })} sub={pct(r.stopPct, { sign: false })} />
              {r.rr != null ? <Kpi label="Reward : risk" value={`${r.rr.toFixed(2)} : 1`} sub={r.rewardDollars ? usd(r.rewardDollars, { sign: true }) : ''} cls={r.rr >= 2 ? 'is-up' : ''} /> : null}
            </div>
            {r.cappedBy ? <p className="myr-warn">Capped at {maxPos}% of the account. Without the cap it would be {Math.floor(r.riskDollars / r.perShare).toLocaleString()} shares; a stop this tight makes for a very large position.</p> : null}
            {r.stopPct < 0.005 ? <p className="myr-warn">A stop under 0.5% away is easily hit by normal price noise and gaps.</p> : null}
            {held ? <p className="myr-warn">You already hold {usd(held)} of {symbol.trim().toUpperCase()}. With this trade it would be {usd(held + r.positionValue)} ({pct((held + r.positionValue) / accountValue, { sign: false })} of the account).</p> : null}
            <p className="mys-muted">Stops can gap: if price opens past your stop you can lose more than this. Risking 0.5–1% a trade means a run of 10 losers costs about 5–10%.</p>
          </>
        )}
      </section>
    </div>
  );
}

/* Concentration ------------------------------------------------------------ */

function ConcentrationRisk({ onNavigate }) {
  const pos = useLoad(() => getMyPositions(ALL_ACCOUNTS), []);
  const bal = useLoad(() => getMyAccountBalances('1M'), []);
  const [shock, setShock] = useState(-20);
  const data = useMemo(() => {
    if (!pos.data || !bal.data) return null;
    const equity = bal.data.reduce((s, a) => s + (Number(a.equity) || 0), 0);
    const invested = pos.data.positions.reduce((s, p) => s + Math.abs(Number(p.marketValue) || 0), 0);
    return concentration(pos.data.positions, { cash: Math.max(0, equity - invested) });
  }, [pos.data, bal.data]);
  const status = <Status state={pos.loading || bal.loading ? { loading: true } : (pos.error ? pos : bal)} onNavigate={onNavigate} empty={!(bal.data || []).length} />;
  if (pos.loading || bal.loading || pos.error || bal.error || !(bal.data || []).length) return status;

  const cash = data.total - data.invested;
  const loss = stressLoss(data.rows, shock / 100);
  const warnings = [];
  if (data.top1 > 0.2) warnings.push(`${data.rows[0].symbol} is ${pct(data.top1, { sign: false })} of everything. A 30% drop in it alone would cost ${usd(data.rows[0].value * 0.3)}.`);
  if (data.top5 > 0.6 && data.rows.length > 5) warnings.push(`Your top 5 holdings are ${pct(data.top5, { sign: false })} of the total.`);
  if (data.rows.length && data.effectiveN < 5) warnings.push(`Your money behaves like ${data.effectiveN.toFixed(1)} equal-sized positions, which is not much spread.`);

  return (
    <>
      <section className="myr-card">
        <div className="myr-kpis">
          <Kpi label="Total value" value={usd(data.total)} />
          <Kpi label="Largest holding" value={pct(data.top1, { sign: false })} sub={data.rows[0]?.symbol || ''} cls={data.top1 > 0.2 ? 'is-down' : ''} />
          <Kpi label="Top 5 holdings" value={pct(data.top5, { sign: false })} />
          <Kpi label="Acts like" value={`${data.effectiveN ? data.effectiveN.toFixed(1) : '—'} positions`} sub={`of ${data.rows.length}`} />
          <Kpi label="Cash" value={usd(cash)} sub={data.total ? pct(cash / data.total, { sign: false }) : ''} />
        </div>
        {warnings.map((w) => <p key={w} className="myr-warn">{w}</p>)}
        {(pos.data.unsupported || []).length ? <p className="mys-muted">Holdings aren’t available for {pos.data.unsupported.map(cleanAccountName).join(', ')}, so they count as cash here.</p> : null}
      </section>
      <div className="prk-grid">
        <section className="myr-card">
          <div className="myr-card-head"><h3>Holdings by weight</h3></div>
          {!data.rows.length ? <p className="mys-muted">No open positions.</p> : (
            <ul className="prk-bars">
              {data.rows.slice(0, 25).map((r) => (
                <li key={r.symbol} title={r.accounts.map(cleanAccountName).join(', ')}>
                  <span className="prk-bar-sym">{r.symbol}</span>
                  <span className="prk-bar-track"><i style={{ width: `${Math.max(1, (r.weight / (data.top1 || 1)) * 100)}%` }} className={r.weight > 0.2 ? 'is-hot' : ''} /></span>
                  <span className="prk-bar-pct">{pct(r.weight, { sign: false })}</span>
                  <span className="prk-bar-val">{usd(r.value, { compact: true })}</span>
                </li>
              ))}
            </ul>
          )}
          {data.rows.length > 25 ? <p className="mys-muted">And {data.rows.length - 25} smaller holdings.</p> : null}
        </section>
        <section className="myr-card">
          <div className="myr-card-head"><h3>Stress test</h3></div>
          <label className="prk-slider">If everything you hold fell <b>{Math.abs(shock)}%</b>
            <input type="range" min="-50" max="-5" step="5" value={shock} onChange={(e) => setShock(Number(e.target.value))} />
          </label>
          <div className="prk-answer is-down">
            <span>You’d lose about</span>
            <b>{usd(Math.abs(loss))}</b>
            <small>{data.total ? pct(loss / data.total) : ''} of your total, with cash unchanged</small>
          </div>
          <p className="mys-muted">For reference: the S&amp;P 500 fell about 34% in early 2020 and 25% in 2022. Single stocks and crypto often fall much more. This assumes every holding falls the same amount.</p>
        </section>
      </div>
    </>
  );
}

/* Drawdown & VaR ----------------------------------------------------------- */

function DrawdownRisk({ onNavigate }) {
  const [range, setRange] = useState('1Y');
  const [account, setAccount] = useState(ALL_ACCOUNTS);
  const perf = useLoad(() => getMyPerformance(account, range), [account, range]);
  const all = useLoad(() => getMyPerformance(ALL_ACCOUNTS, '1M'), []);
  const accounts = all.data?.accounts || [];
  const r = useMemo(() => (perf.data?.points?.length > 1 ? equityRisk(perf.data.points) : null), [perf.data]);

  const head = (
    <div className="prk-controls">
      <select value={account} onChange={(e) => setAccount(e.target.value)} aria-label="Account">
        <option value={ALL_ACCOUNTS}>All connected accounts</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{cleanAccountName(a.name)}</option>)}
      </select>
      <div className="mydw-pills">
        {['3M', '6M', '1Y', 'All'].map((v) => <button key={v} type="button" className={range === v ? 'active' : ''} onClick={() => setRange(v)}>{v}</button>)}
      </div>
    </div>
  );
  const empty = !perf.loading && !perf.error && !(perf.data?.accounts || []).length;
  if (perf.loading || perf.error || empty) return <>{head}<Status state={perf} onNavigate={onNavigate} empty={empty} /></>;
  if (!r) return <>{head}<div className="myr-state"><span>Not enough history in this period yet.</span></div></>;

  return (
    <>
      {head}
      <section className="myr-card">
        <div className="myr-kpis">
          <Kpi label="Now" value={usd(r.current)} />
          <Kpi label="Below the high" value={pct(r.currentDd)} sub={r.peakDate ? `high ${usd(r.peak, { compact: true })} on ${shortDate(r.peakDate)}` : ''} cls={r.currentDd < 0 ? 'is-down' : ''} />
          <Kpi label="Worst drawdown" value={pct(r.maxDd)} sub={r.maxDdDate ? `bottom ${shortDate(r.maxDdDate)}` : ''} cls="is-down" />
          <Kpi label="Daily swing (volatility)" value={pct(r.dailyVol, { sign: false })} sub={`${pct(r.annualVol, { sign: false })} a year`} />
          <Kpi
            label="Bad day (95% VaR)"
            value={r.varDollars == null ? '—' : usd(r.varDollars)}
            sub={r.varPct == null ? 'needs 20+ days' : pct(r.varPct)}
            cls="is-down"
          />
          <Kpi label="Average of the worst 5%" value={r.cvarDollars == null ? '—' : usd(r.cvarDollars)} sub={r.cvarPct == null ? '' : pct(r.cvarPct)} cls="is-down" />
          <Kpi label="Worst day" value={r.worst ? pct(r.worst.ret) : '—'} sub={r.worst ? `${usd(r.worst.pnl, { sign: true })} · ${shortDate(r.worst.date)}` : ''} cls="is-down" />
          <Kpi label="Down days" value={`${r.downDays} of ${r.days}`} sub={r.days ? pct(r.downDays / r.days, { sign: false }) : ''} />
        </div>
        {r.varDollars != null ? (
          <p className="mys-muted">
            On about 1 day in 20, your accounts lost {usd(Math.abs(r.varDollars))} or more ({pct(r.varPct, { sign: false }).replace('-', '')}) based on this period’s history.
            When that happened, the average loss was {usd(Math.abs(r.cvarDollars || 0))}. Past swings don’t cap future ones.
          </p>
        ) : null}
      </section>
      <section className="myr-card">
        <div className="myr-card-head"><h3>Below the high</h3><span className="mys-muted">Deposits and withdrawals aren’t counted as gains or losses.</span></div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={r.ddSeries} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
            <XAxis dataKey="date" tickFormatter={shortDate} tick={axisTick} minTickGap={40} />
            <YAxis tickFormatter={(v) => pct(v, { sign: false })} tick={axisTick} width={54} />
            <ReferenceLine y={0} stroke="rgba(148,163,184,0.35)" />
            <Tooltip formatter={(v) => pct(v)} labelFormatter={niceDate} contentStyle={tipStyle} />
            <Area type="monotone" dataKey="dd" name="Below the high" stroke={DOWN} fill="rgba(239,68,68,0.18)" strokeWidth={1.5} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </section>
      {(perf.data.perAccount || []).length > 1 && account === ALL_ACCOUNTS ? (
        <section className="myr-card">
          <div className="myr-card-head"><h3>By account</h3></div>
          <div className="myr-table-wrap">
            <table className="myr-table is-compact">
              <thead><tr><th>Account</th><th>Value</th><th>Below high</th><th>Worst drawdown</th><th>Yearly volatility</th><th>95% VaR</th></tr></thead>
              <tbody>
                {perf.data.perAccount.map((a) => {
                  const x = equityRisk(a.points);
                  return (
                    <tr key={a.id}>
                      <td><span className="myr-name"><BrokerMark broker={a.broker} name={a.name} size={18} /><span className="myr-name-text">{cleanAccountName(a.name)}</span></span></td>
                      <td>{usd(x.current)}</td>
                      <td className={tone(x.currentDd)}>{pct(x.currentDd)}</td>
                      <td className="is-down">{pct(x.maxDd)}</td>
                      <td>{pct(x.annualVol, { sign: false })}</td>
                      <td className="is-down">{x.varDollars == null ? '—' : usd(x.varDollars)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}

/* Stop planner ------------------------------------------------------------- */

const STOP_METHODS = [
  { value: 'percent', label: '% below price', unit: '%' },
  { value: 'risk', label: 'Max $ loss', unit: '$' },
  { value: 'price', label: 'Stop price', unit: '$' },
];

function StopPlanner({ onNavigate }) {
  const pos = useLoad(() => getMyPositions(ALL_ACCOUNTS), []);
  const bal = useLoad(() => getMyAccountBalances('1M'), []);
  const [defaultPct, setDefaultPct] = useStoredState('risklo.stopPlanner.defaultPct', 8);
  const [rules, setRules] = useStoredState('risklo.stopPlanner.rules', {});
  const holdings = useMemo(() => (pos.data ? holdingsBySymbol(pos.data.positions) : []), [pos.data]);
  const equity = (bal.data || []).reduce((s, a) => s + (Number(a.equity) || 0), 0);

  const empty = !(bal.data || []).length;
  if (pos.loading || bal.loading || pos.error || bal.error || empty) {
    return <Status state={pos.loading || bal.loading ? { loading: true } : (pos.error ? pos : bal)} onNavigate={onNavigate} empty={empty} />;
  }
  if (!holdings.length) return <div className="myr-state"><strong>No open positions</strong><span>Once your accounts hold stocks or crypto, plan their stops here.</span></div>;

  const ruleFor = (sym) => rules[sym] || { method: 'percent', value: defaultPct };
  const setRule = (sym, patch) => setRules((r) => ({ ...r, [sym]: { ...ruleFor(sym), ...patch } }));
  const plans = holdings.map((h) => ({ h, rule: ruleFor(h.symbol), plan: planStop(h, ruleFor(h.symbol)) }));
  const totalLoss = plans.reduce((s, p) => s + (p.plan?.lossFromNow || 0), 0);
  const covered = plans.filter((p) => p.plan).length;
  const locked = plans.filter((p) => p.plan?.locksInGain).length;
  const triggered = plans.filter((p) => p.plan?.triggered);

  return (
    <>
      <section className="myr-card">
        <div className="myr-kpis">
          <Kpi label="If every stop is hit" value={usd(-totalLoss)} sub={equity ? `${pct(-totalLoss / equity)} of your accounts` : ''} cls="is-down" />
          <Kpi label="Holdings with a stop" value={`${covered} of ${holdings.length}`} />
          <Kpi label="Stops that lock in a gain" value={`${locked}`} sub={locked ? 'above your average cost' : ''} cls={locked ? 'is-up' : ''} />
        </div>
        <div className="prk-controls">
          <label className="prk-inline">Default stop
            <input type="number" min="0.5" max="90" step="0.5" value={defaultPct} onChange={(e) => setDefaultPct(Number(e.target.value) || 0)} />
            % below the price
          </label>
          {Object.keys(rules).length ? <button type="button" className="mys-btn" onClick={() => setRules({})}>Reset all to default</button> : null}
        </div>
        {triggered.length ? <p className="myr-warn">{triggered.map((p) => p.h.symbol).join(', ')} {triggered.length === 1 ? 'is' : 'are'} already past the stop you set.</p> : null}
      </section>
      <section className="myr-card">
        <div className="myr-table-wrap">
          <table className="myr-table is-compact prk-stop-table">
            <thead>
              <tr><th>Holding</th><th>Qty</th><th>Price</th><th>Avg cost</th><th>Stop by</th><th aria-label="Stop setting" /><th>Stop at</th><th>Away</th><th>Lose from here</th><th>vs. your cost</th></tr>
            </thead>
            <tbody>
              {plans.map(({ h, rule, plan }) => (
                <tr key={h.symbol}>
                  <td title={h.accounts.map(cleanAccountName).join(', ')}><b>{h.symbol}</b>{h.quantity < 0 ? <span className="prk-tag">Short</span> : null}</td>
                  <td>{Math.abs(h.quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                  <td>{usd(h.price, { cents: true })}</td>
                  <td>{h.avgEntry ? usd(h.avgEntry, { cents: true }) : '—'}</td>
                  <td>
                    <select value={rule.method} onChange={(e) => setRule(h.symbol, { method: e.target.value, value: e.target.value === 'price' ? Number((h.price * (h.quantity > 0 ? 0.92 : 1.08)).toFixed(2)) : e.target.value === 'risk' ? Math.round(Math.abs(h.value) * 0.08) : defaultPct })} aria-label={`How to set the stop for ${h.symbol}`}>
                      {STOP_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="prk-num" type="number" min="0" step="any" value={rule.value} onChange={(e) => setRule(h.symbol, { value: e.target.value })} aria-label={`Stop setting for ${h.symbol}`} />
                  </td>
                  <td>{plan ? usd(plan.stop, { cents: true }) : '—'}</td>
                  <td>{plan ? pct(plan.distancePct, { sign: false }) : '—'}</td>
                  <td className="is-down">{plan ? (plan.triggered ? 'Past stop' : usd(-plan.lossFromNow)) : '—'}</td>
                  <td className={plan?.pnlAtStop != null ? tone(plan.pnlAtStop) : ''}>
                    {plan?.pnlAtStop != null ? usd(plan.pnlAtStop, { sign: true }) : '—'}
                    {plan?.locksInGain ? <span className="prk-tag is-up">Locks gain</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mys-muted">
          A % stop shows where a trailing stop would sit today; it moves up as the price rises. Stops can fill below the stop price when a stock gaps.
          RiskLo doesn’t place orders: set these in your broker. Your plan is saved in this browser.
        </p>
      </section>
    </>
  );
}

/* Rebalancing -------------------------------------------------------------- */

function Rebalancer({ onNavigate }) {
  const [account, setAccount] = useState(ALL_ACCOUNTS);
  const pos = useLoad(() => getMyPositions(account), [account]);
  const bal = useLoad(() => getMyAccountBalances('1M'), []);
  const [targetsByScope, setTargetsByScope] = useStoredState('risklo.rebalance.targets', {});
  const [band, setBand] = useStoredState('risklo.rebalance.band', 0);
  const [buyOnly, setBuyOnly] = useStoredState('risklo.rebalance.buyOnly', false);
  const [fractional, setFractional] = useStoredState('risklo.rebalance.fractional', false);
  const [addCash, setAddCash] = useState('');
  const [newSym, setNewSym] = useState('');

  const accounts = bal.data || [];
  const holdings = useMemo(() => (pos.data ? holdingsBySymbol(pos.data.positions).filter((h) => !(h.quantity < 0) && h.value > 0) : []), [pos.data]);
  const equity = account === ALL_ACCOUNTS
    ? accounts.reduce((s, a) => s + (Number(a.equity) || 0), 0)
    : Number(accounts.find((a) => a.id === account)?.equity) || 0;
  const invested = holdings.reduce((s, h) => s + h.value, 0);
  const cash = Math.max(0, equity - invested);
  const total = invested + cash;

  const currentWeights = useMemo(() => {
    const t = {};
    holdings.forEach((h) => { t[h.symbol] = total ? Math.round((h.value / total) * 1000) / 10 : 0; });
    t.CASH = total ? Math.round((cash / total) * 1000) / 10 : 0;
    return t;
  }, [holdings, total, cash]);
  // Saved targets, plus today's weight for anything bought since they were
  // saved, so a new holding is never sold off just for missing a target.
  const targets = useMemo(() => {
    const saved = targetsByScope[account];
    if (!saved) return currentWeights;
    const merged = { ...saved };
    holdings.forEach((h) => { if (merged[h.symbol] === undefined) merged[h.symbol] = currentWeights[h.symbol]; });
    return merged;
  }, [targetsByScope, account, currentWeights, holdings]);
  const setTargets = (next) => setTargetsByScope((m) => ({ ...m, [account]: typeof next === 'function' ? next(targets) : next }));
  const setTarget = (sym, v) => setTargets((t) => ({ ...t, [sym]: v === '' ? '' : Number(v) }));

  const empty = !accounts.length;
  const picker = (
    <div className="prk-controls">
      <select value={account} onChange={(e) => setAccount(e.target.value)} aria-label="Account">
        <option value={ALL_ACCOUNTS}>All connected accounts</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{cleanAccountName(a.name)}</option>)}
      </select>
    </div>
  );
  if (pos.loading || bal.loading || pos.error || bal.error || empty) {
    return <>{!empty && !bal.loading ? picker : null}<Status state={pos.loading || bal.loading ? { loading: true } : (pos.error ? pos : bal)} onNavigate={onNavigate} empty={empty} /></>;
  }

  const numericTargets = Object.fromEntries(Object.entries(targets).map(([k, v]) => [k, Number(v) || 0]));
  const r = rebalance(holdings, cash, numericTargets, { band: Number(band) || 0, buyOnly, addCash: Number(addCash) || 0, fractional });
  const sumOff = Math.abs(r.targetSum - 100) > 0.05;
  const equal = () => {
    const n = holdings.length;
    const cashPct = Number(targets.CASH) || 0;
    const each = n ? Math.floor(((100 - cashPct) / n) * 10) / 10 : 0;
    const next = { CASH: cashPct };
    holdings.forEach((h) => { next[h.symbol] = each; });
    if (n) next[holdings[0].symbol] = Math.round((100 - cashPct - each * (n - 1)) * 10) / 10;
    setTargets(next);
  };
  const addSymbol = (e) => {
    e.preventDefault();
    const sym = newSym.trim().toUpperCase();
    if (!sym || sym === 'CASH') return;
    setTargets((t) => ({ ...t, [sym]: t[sym] ?? 0 }));
    setNewSym('');
  };
  const removeSymbol = (sym) => setTargets((t) => { const n = { ...t }; delete n[sym]; return n; });

  return (
    <>
      {picker}
      <section className="myr-card">
        <div className="prk-controls">
          <button type="button" className="mys-btn" onClick={() => setTargets(currentWeights)}>Start from current weights</button>
          <button type="button" className="mys-btn" onClick={equal}>Equal weight</button>
          <label className="prk-inline">Only trade if off by
            <select value={band} onChange={(e) => setBand(Number(e.target.value))}>
              {[0, 1, 2, 5, 10].map((b) => <option key={b} value={b}>{b ? `${b}+ points` : 'any amount'}</option>)}
            </select>
          </label>
          <label className="prk-inline"><input type="checkbox" checked={buyOnly} onChange={(e) => setBuyOnly(e.target.checked)} /> Buy only (no sells)</label>
          <label className="prk-inline"><input type="checkbox" checked={fractional} onChange={(e) => setFractional(e.target.checked)} /> Fractional shares</label>
          <label className="prk-inline">Adding cash $
            <input type="number" min="0" value={addCash} onChange={(e) => setAddCash(e.target.value)} placeholder="0" />
          </label>
        </div>
        {sumOff ? <p className="myr-warn">Your targets add up to {r.targetSum.toFixed(1)}%, not 100%. {r.targetSum < 100 ? 'The rest stays as cash.' : 'Lower some targets.'}</p> : null}
      </section>
      <section className="myr-card">
        <div className="myr-table-wrap">
          <table className="myr-table is-compact prk-rebal-table">
            <thead><tr><th>Holding</th><th>Value</th><th>Now</th><th>Target %</th><th>Off by</th><th>Do this</th><th>After</th><th aria-label="Remove" /></tr></thead>
            <tbody>
              {r.trades.map((t) => {
                const after = r.total ? (t.value + t.amount) / r.total : 0;
                const held = holdings.some((h) => h.symbol === t.symbol);
                return (
                  <tr key={t.symbol}>
                    <td><b>{t.symbol}</b>{!held ? <span className="prk-tag">New</span> : null}</td>
                    <td>{usd(t.value)}</td>
                    <td>{pct(t.weight, { sign: false })}</td>
                    <td><input className="prk-num" type="number" min="0" max="100" step="0.5" value={targets[t.symbol] ?? 0} onChange={(e) => setTarget(t.symbol, e.target.value)} aria-label={`Target for ${t.symbol}`} /></td>
                    <td className={Math.abs(t.drift) >= 5 ? 'is-down' : ''}>{t.drift >= 0 ? '+' : ''}{t.drift.toFixed(1)} pts</td>
                    <td className={t.action === 'buy' ? 'is-up' : t.action === 'sell' ? 'is-down' : ''}>
                      {t.action === 'hold' ? (t.diff && Math.abs(t.drift) < band ? 'Within band' : 'Hold') : (
                        <>
                          {t.action === 'buy' ? 'Buy' : 'Sell'} {t.shares != null ? `${Math.abs(t.shares).toLocaleString(undefined, { maximumFractionDigits: 4 })} ` : ''}
                          <span className="prk-sub">{t.shares != null ? '≈ ' : ''}{usd(Math.abs(t.amount))}</span>
                          {!t.price ? <span className="prk-sub"> (no price yet: buy this dollar amount)</span> : null}
                        </>
                      )}
                    </td>
                    <td>{pct(after, { sign: false })}</td>
                    <td>{!held ? <button type="button" className="prk-x" aria-label={`Remove ${t.symbol}`} onClick={() => removeSymbol(t.symbol)}>×</button> : null}</td>
                  </tr>
                );
              })}
              <tr>
                <td><b>Cash</b></td>
                <td>{usd(r.cashNow)}</td>
                <td>{pct(r.total ? r.cashNow / r.total : 0, { sign: false })}</td>
                <td><input className="prk-num" type="number" min="0" max="100" step="0.5" value={targets.CASH ?? 0} onChange={(e) => setTarget('CASH', e.target.value)} aria-label="Target for cash" /></td>
                <td />
                <td />
                <td>{pct(r.total ? r.cashAfter / r.total : 0, { sign: false })}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <form className="prk-controls prk-add" onSubmit={addSymbol}>
          <input value={newSym} onChange={(e) => setNewSym(e.target.value)} placeholder="Add a symbol you don’t own yet (e.g. VTI)" aria-label="Symbol to add" />
          <button type="submit" className="mys-btn">Add</button>
        </form>
        <div className="myr-kpis prk-summary">
          <Kpi label="Buy" value={usd(r.bought)} cls="is-up" />
          <Kpi label="Sell" value={usd(r.sold)} cls={r.sold ? 'is-down' : ''} />
          <Kpi label="Cash after" value={usd(r.cashAfter)} cls={r.cashAfter < 0 ? 'is-down' : ''} />
        </div>
        {r.cashAfter < -1 ? <p className="myr-warn">These buys need {usd(-r.cashAfter)} more cash than you have. Raise the cash target, add cash, or turn on Buy only.</p> : null}
        <p className="mys-muted">
          {account === ALL_ACCOUNTS ? 'Across all accounts: place each trade in an account that holds (or can buy) it. ' : ''}
          Selling can trigger taxes in a taxable account; Buy only avoids that by putting new cash into what’s under target.
          Prices are from your last sync. RiskLo doesn’t place orders. Targets are saved in this browser.
        </p>
      </section>
    </>
  );
}

export default function PortfolioRiskPage({ tool, isPro, onNavigate, onUpgrade }) {
  const meta = PORTFOLIO_RISK_TOOLS[tool] || PORTFOLIO_RISK_TOOLS['position-sizer'];
  let body;
  if (tool === 'position-sizer') body = <PositionSizer isPro={isPro} />;
  else if (!isPro) body = <ProUpsell what={meta.title} onUpgrade={onUpgrade} />;
  else if (tool === 'concentration-risk') body = <ConcentrationRisk onNavigate={onNavigate} />;
  else if (tool === 'stop-planner') body = <StopPlanner onNavigate={onNavigate} />;
  else if (tool === 'rebalance') body = <Rebalancer onNavigate={onNavigate} />;
  else body = <DrawdownRisk onNavigate={onNavigate} />;
  return (
    <>
      <PageHeading title={meta.title} description={meta.description} />
      <div className="myr-page prk-page">
        <p className="prk-scope">For stocks, ETFs and crypto in your brokerage accounts. Trading futures in a prop firm evaluation? Use the prop firm tools under Risk Tools.</p>
        {body}
      </div>
    </>
  );
}
